import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Docmnt, ModelClass } from '/imports/types';
import { strings } from '/imports/strings';
import { MethodDeclaration } from '/imports/methods';
import { Form } from '/imports/ui/Form';
import { FieldSpec, FormBuilder } from '/imports/ui/FormBuilder';
import { OverlayAbsolute } from '/imports/ui/OverlayAbsolute';
import { Button } from '/imports/ui/Button';
import { Loading } from '/imports/ui/Loading';
import { panelSurface } from '/imports/ui/Panel';
import { deleteSavedFormForKey } from '/imports/api/savedForms';

export interface AutoFormProps<T extends Docmnt = any> {
  modelClass?: ModelClass<T>;
  createMethod?: MethodDeclaration<Partial<T>, string>;
  updateMethod?: MethodDeclaration<Partial<T>, string>;
  deleteMethod?: MethodDeclaration<string, void>;
  form: FormSpec<T> | (() => FormSpec<T>);
  document?: T;
  update: Partial<T>;
  setUpdate: (value: React.SetStateAction<Partial<T>>) => void;
  header?: React.ReactNode | ((setUpdate: (value: React.SetStateAction<Partial<T>>) => void, update: Partial<T>, error: any) => React.ReactNode);
  footer?: React.ReactNode | ((setUpdate: (value: React.SetStateAction<Partial<T>>) => void, update: Partial<T>, error: any) => React.ReactNode);
  redirect?: string | null;
  submitButtonText?: string;
  save?: (update: Partial<T>, document?: T) => void | Promise<void>;
  beforeSave?: (update: Partial<T>, document?: T) => boolean | Promise<boolean>;
  onSave?: (_id: string) => void | Promise<void>;
  onError?: (error: any) => void | Promise<void>;
  cacheKey?: string;
  extraButtons?: React.ReactNode;
  submitInput?: boolean;
}

export const AutoForm = <T extends Docmnt>
  (props: AutoFormProps<T>) => {
  const { form, document, update, setUpdate, modelClass, createMethod, updateMethod, deleteMethod, redirect, header, footer,
    submitButtonText, onSave, onError, beforeSave, cacheKey, extraButtons, submitInput } = props;
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<any>();
  const _id = update?._id || document?._id;
  const isNew = _id === 'new';
  const save = async () => {
    try {
      if (props.save) return await props.save(update, document);
      if (beforeSave) {
        const shouldContinue = await beforeSave(update, document);
        if (!shouldContinue) return;
      }
      const method = isNew ? createMethod || modelClass?.createMethod : updateMethod || modelClass?.updateMethod;
      if (!method) return console.warn('No method is configured for the save button of this form');
      setSubmitting(true);
      const _id = await method.call(update);
      setError(undefined);
      toast.success(strings.Saved());
      redirect && navigate(redirect);
      onSave?.(_id);
      cacheKey && deleteSavedFormForKey.call(cacheKey);
    }
    catch (error: any) {
      console.log(error);
      console.log(onError);
      if (error?.reason === 'validationError') setError(JSON.parse(error.details));
      else if (onError) onError(error);
      else toast.error(`${error}`);
    }
    finally {
      setSubmitting(false);
    }
  };
  const deleteDocument = async () => {
    try {
      _id && await (deleteMethod || modelClass?.deleteMethod)?.call(String(_id));
      redirect ? navigate(redirect) : navigate(-1);
    }
    catch (error: any) {
      console.log(error);
      if (onError) onError(error);
      else toast.error(`${error}`);
    }
  };
  return (
    // The form sits on a panel, like every other surface in the console, and
    // the action row is a footer band flush with the panel edge. The fields get
    // the padding rather than the root, so the band can meet the border without
    // a negative margin: this wrapper has no padding of its own, so an -mx-4
    // here would push the row outside the form and scroll the page sideways.
    // shrink-0 is load-bearing: this panel is a direct flex child of PageBody
    // (flex-col, min-h-0, flex-1), so with the default flex-shrink:1 it gets
    // squashed below its content height on any viewport shorter than the form.
    // Combined with the overflow-hidden here -- which exists so the sticky
    // footer band clips against the panel's rounded corners -- everything past
    // the fold was silently cut off rather than scrolling.
    <div className={`shrink-0 overflow-hidden ${panelSurface} [&_h2]:m-0 [&_h2]:-mb-4`}>
      <Form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); save(); return false; }} submitInput={submitInput}>
        <div className="p-4">
          {typeof header === 'function' ? header(setUpdate, update, error) : header}
          <FormBuilder
            document={document}
            update={update}
            setUpdate={setUpdate}
            error={error}
            form={typeof form === 'function' ? form() : form}
            cacheKey={cacheKey}
          />
          {typeof footer === 'function' ? footer(setUpdate, update, error) : footer}
        </div>
        {/*
          Save was `w-full flex-1`, a full-width green bar across the whole
          form. It stays full width on mobile, where that reads as a thumb
          target. Delete sits at the far left so it is never adjacent to Save.
        */}
        <div className="buttons sticky bottom-0 flex items-center gap-2 border-t border-soft bg-panel px-4 py-3">
          {_id && !isNew && deleteMethod && <Button className="btn secondary delete mr-auto border-sell! text-sell! hover:bg-sell! hover:text-on-primary!" onClick={() => deleteDocument()}>{document?.active ? strings.Deactivate() : strings.Delete()}</Button>}
          {extraButtons || null}
          {submitInput !== false && <Button type="submit" className="btn primary save ml-auto mobile:w-full mobile:flex-1">{submitting ? <Loading /> : submitButtonText || strings.Save()}</Button> || null}
        </div>
        {submitting && <OverlayAbsolute />}
      </Form>
    </div>
  );
};

export type FormSpec<T extends Docmnt> = (FieldSpec<T> | React.ReactNode)[][];

export const autoForm = <T extends Docmnt>(props: Omit<AutoFormProps<T>, 'update' | 'setUpdate'> & {
  defaultUpdate?: Partial<T>,
  update?: Partial<T>,
  setUpdate?: (value: React.SetStateAction<Partial<T>>) => void
}) => {
  const { document, defaultUpdate, update, setUpdate } = props;
  const _id = document?._id || 'new';
  const isNew = _id === 'new';
  const _defaultUpdate = (!isNew ? { _id } : defaultUpdate || { _id, active: true }) as Partial<T>;
  const [_update, _setUpdate] = update && setUpdate ? [update, setUpdate] : useState<Partial<T>>(_defaultUpdate);
  useEffect(() => _setUpdate(_defaultUpdate as Partial<T>), [document]);
  const component = (
    <AutoForm
      {...props}
      update={_update}
      setUpdate={_setUpdate}
    />
  );
  return [component, _setUpdate, _update] as [React.JSX.Element, (value: React.SetStateAction<Partial<T>>) => void, Partial<T>];
};

export const formConfig = <T extends Docmnt>(config: FormSpec<T>, deps: any[] = []) => {
  return useMemo<FormSpec<T>>(() => config, deps);
};